// How the Admin → Config page shows a config value and reads it back from what was typed.
// Kept apart from the page so the rules can be unit tested.

/** which control the page draws for a value */
export enum ConfigValueKind {
  Toggle = 'Toggle',
  Text = 'Text',
  Json = 'Json',
}

/** the result of reading typed text back into a value: the value, or why it could not be read */
export type ParsedConfigValue = { value: unknown } | { error: string };

/** a boolean gets a toggle, a list or object gets a JSON box, anything else a text box */
export function describeConfigValueKind(value: unknown): ConfigValueKind {
  if (typeof value === 'boolean') return ConfigValueKind.Toggle;
  if (value !== null && typeof value === 'object') return ConfigValueKind.Json;
  return ConfigValueKind.Text;
}

/** the text shown in the box for a value: a list or object as indented JSON, anything else as plain text */
export function formatConfigValue(value: unknown): string {
  if (describeConfigValueKind(value) === ConfigValueKind.Json) return JSON.stringify(value, null, 2);
  return String(value);
}

/**
 * read typed text back into a value. A JSON box must hold valid JSON: anything else is
 * refused with the parser's reason, so a typo can never replace a list with text.
 * In any other box 'true' / 'false' become booleans, a number becomes a number, and
 * anything else stays text.
 */
export function parseConfigValue(text: string, kind: ConfigValueKind): ParsedConfigValue {
  if (kind === ConfigValueKind.Json) {
    try {
      return { value: JSON.parse(text) };
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'the text is not valid JSON' };
    }
  }
  if (text === 'true') return { value: true };
  if (text === 'false') return { value: false };
  const num = Number(text);
  if (!isNaN(num) && text.trim() !== '') return { value: num };
  return { value: text };
}

/** the text one JSON box holds while it is being edited, and why it does not parse if it does not */
export interface JsonDraft {
  text: string;
  error?: string;
}

/**
 * the JSON boxes being edited on the page, per config and field. The global value is
 * the field `null`; a user override is its user id. Save asks hasError for its card
 * and writes nothing while one of its boxes does not parse.
 */
export class ConfigJsonDrafts {
  private drafts = new Map<string, JsonDraft>();

  get(configId: string, userId: string | null): JsonDraft | undefined {
    return this.drafts.get(this.key(configId, userId));
  }

  // valid text comes back as the new value; invalid text is kept with its reason and comes back as an error
  edit(configId: string, userId: string | null, text: string): ParsedConfigValue {
    const parsed = parseConfigValue(text, ConfigValueKind.Json);
    this.drafts.set(this.key(configId, userId), 'error' in parsed ? { text, error: parsed.error } : { text });
    return parsed;
  }

  forget(configId: string, userId: string | null) {
    this.drafts.delete(this.key(configId, userId));
  }

  hasError(configId: string): boolean {
    return [...this.drafts].some(([key, draft]) => key.startsWith(this.prefix(configId)) && draft.error !== undefined);
  }

  // after a save the stored values are the truth, so every box of that config shows them afresh
  clear(configId: string) {
    for (const key of [...this.drafts.keys()]) {
      if (key.startsWith(this.prefix(configId))) this.drafts.delete(key);
    }
  }

  private prefix(configId: string): string {
    return `${configId}|`;
  }

  private key(configId: string, userId: string | null): string {
    return this.prefix(configId) + (userId === null ? 'global' : `user|${userId}`);
  }
}
